/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Application top bar — Figma node 3281:98498 (chat home).
// Dimensions: 1920×56. Background `surface-app`, full-width row
// spanning the viewport. The 300×32 search pill is left-padded so it
// lines up with the main card's left edge (x = sidebar width = 72).
//
// Search pill: r=8, bg `surface-panel`, border `border-subtle`, icon +
// placeholder `icon-muted` / `text-tertiary`. Search font: Lato 400 12/17.

import { type ComponentType, useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";
import { Search, StickyNote, X } from "lucide-react-native";

// Structural icon type — accepts any lucide-style icon component
// regardless of which copy of `lucide-react-native`/`react-native` the
// caller resolves (apps/web + apps/desktop pin different RN versions, so
// a nominal `LucideIcon` from one package — including its RN-version-
// specific `defaultProps` — isn't assignable to the other). `any` props
// avoids that nominal clash; rendering only passes size/color/strokeWidth.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TabIconComponent = ComponentType<any>;
// Class-aware scheme (data-theme/.dark) — NOT react-native's OS hook,
// which renders the wrong palette on reload (repo CLAUDE.md rule).
import { useColorScheme } from "../foundations/colorScheme";
import { tokens } from "../design-system/tokens";

export interface TopBarProps {
  readonly searchValue?: string;
  readonly onSearchChange?: (next: string) => void;
  readonly searchPlaceholder?: string;
  /**
   * Width of the sidebar that sits below this top bar. The search
   * input left-pads by this amount so its left edge lines up with
   * the main card under it. Defaults to the 72-px chat-home rail.
   */
  readonly sidebarWidth?: number;
  /**
   * When supplied, the search pill becomes a click-to-open trigger
   * (no inline typing); pressing anywhere on the pill — or the
   * Cmd/Ctrl+K hotkey at the shell level — calls this handler.
   * The right side of the pill shows a "⌘K" / "Ctrl K" badge so
   * the affordance is discoverable.
   *
   * Omit `onOpenSearch` to keep the legacy inline-input behavior
   * (callers wire `searchValue` / `onSearchChange`).
   */
  readonly onOpenSearch?: () => void;
  /**
   * Contextual section tabs shown to the RIGHT of the search pill —
   * Figma design-system top-bar `4087:90367`. The shell passes the Notes
   * tab when the Notes section is active. Each tab carries a note glyph +
   * label + active highlight and drives the router.
   */
  readonly tabs?: ReadonlyArray<{
    readonly key: string;
    readonly label: string;
    readonly active: boolean;
    readonly onPress: () => void;
    /** Per-tab glyph (defaults to a note icon). */
    readonly icon?: TabIconComponent | undefined;
  }>;
}

function TopBarTabs({
  tabs,
  iconColor,
  accent,
}: {
  readonly tabs: NonNullable<TopBarProps["tabs"]>;
  readonly iconColor: string;
  readonly accent: string;
}): JSX.Element {
  return (
    <View className="flex-row items-center gap-xs ml-md" accessibilityRole="tablist">
      {tabs.map((t) => {
        const TabIcon = t.icon ?? StickyNote;
        return (
        <Pressable
          key={t.key}
          accessibilityRole="tab"
          accessibilityState={{ selected: t.active }}
          accessibilityLabel={t.label}
          onPress={t.onPress}
          className={
            "flex-row items-center gap-xs h-[32px] rounded-[8px] px-md " +
            (t.active
              ? "bg-light-surface-panel dark:bg-dark-surface-panel"
              : "hover:bg-light-surface-panel dark:hover:bg-dark-surface-panel")
          }
        >
          <TabIcon size={14} strokeWidth={1.75} color={t.active ? accent : iconColor} />
          <Text
            numberOfLines={1}
            className="font-sans text-[13px] leading-[17px]"
            style={{ color: t.active ? accent : iconColor, fontWeight: t.active ? "600" : "500" }}
          >
            {t.label}
          </Text>
        </Pressable>
        );
      })}
    </View>
  );
}

export function TopBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Describe what you're looking for…",
  sidebarWidth = 72,
  onOpenSearch,
  tabs,
}: TopBarProps): JSX.Element {
  const scheme = useColorScheme();
  const c = tokens.color.semantic[scheme === "dark" ? "dark" : "light"];
  const [internalValue, setInternalValue] = useState("");
  const isControlled = searchValue !== undefined;
  const value = isControlled ? (searchValue as string) : internalValue;
  const setValue = (next: string): void => {
    if (!isControlled) setInternalValue(next);
    onSearchChange?.(next);
  };

  // Click-to-open trigger mode — render the pill as a Pressable
  // stamped with a static placeholder + a hotkey badge. The pill
  // looks the same as the legacy input so the topbar's silhouette is
  // preserved; only the interaction model changes.
  if (onOpenSearch !== undefined) {
    // const hotkeyLabel = Platform.OS === 'macos' || Platform.OS === 'ios' ? '⌘K' : 'Ctrl K';
    return (
      <View
        accessibilityRole="toolbar"
        className="flex-row items-center h-[56px] bg-light-surface-app dark:bg-dark-surface-app"
        style={{ paddingLeft: sidebarWidth + 12, paddingRight: 12 }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Open global search"
          onPress={onOpenSearch}
          className="flex-row items-center w-[300px] h-[32px] rounded-[8px] px-md gap-sm bg-light-surface-panel border border-light-border-subtle dark:bg-dark-surface-panel dark:border-dark-border-subtle hover:border-light-accent-brand dark:hover:border-dark-accent-brand"
        >
          <Search size={13} strokeWidth={1.75} color={c.icon.muted} />
          <Text
            numberOfLines={1}
            className="flex-1 font-sans font-normal text-[12px] leading-[17px] text-light-text-tertiary dark:text-dark-text-tertiary"
          >
            {searchPlaceholder}
          </Text>
          {/* <View
						accessibilityElementsHidden
						className="px-[6px] h-[18px] rounded-[4px] border border-light-border-subtle dark:border-dark-border-subtle items-center justify-center"
					>
						<Text className="font-sans font-medium text-[10px] leading-[14px] text-light-text-tertiary dark:text-dark-text-tertiary">
							{hotkeyLabel}
						</Text>
					</View> */}
        </Pressable>
        {tabs !== undefined && tabs.length > 0 ? (
          <TopBarTabs tabs={tabs} iconColor={c.text.secondary} accent={c.accent.indigo} />
        ) : null}
      </View>
    );
  }

  return (
    <View
      accessibilityRole="toolbar"
      className="flex-row items-center h-[56px] bg-light-surface-app dark:bg-dark-surface-app"
      // Left padding equals the sidebar width so the search starts
      // where the main card starts — matches the Figma frame's
      // x=84 search position next to the 72-wide nav-bar. Tailwind
      // arbitrary values can't reference a prop-driven number, so
      // this `paddingLeft` is the documented inline-style fallback
      // per the project's Tailwind-first rule.
      style={{ paddingLeft: sidebarWidth + 12, paddingRight: 12 }}
    >
      <View
        accessibilityLabel="Global search"
        className="flex-row items-center w-[300px] h-[32px] rounded-[8px] px-md gap-sm bg-light-surface-panel border border-light-border-subtle dark:bg-dark-surface-panel dark:border-dark-border-subtle focus-within:border-light-accent-brand dark:focus-within:border-dark-accent-brand"
      >
        <Search size={13} strokeWidth={1.75} color={c.icon.muted} />
        <TextInput
          value={value}
          onChangeText={setValue}
          placeholder={searchPlaceholder}
          placeholderTextColor={c.text.tertiary}
          {...({ enableFocusRing: false } as { enableFocusRing: boolean })}
          className="flex-1 font-sans font-normal text-[12px] leading-[17px] text-light-text-primary dark:text-dark-text-primary outline-none"
          accessibilityLabel="ping_topbar_search"
        />
        {value.length > 0 ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            onPress={() => setValue("")}
            className="w-[14px] h-[14px] items-center justify-center"
          >
            <X size={12} color={c.icon.muted} />
          </Pressable>
        ) : null}
      </View>
      {tabs !== undefined && tabs.length > 0 ? (
        <TopBarTabs tabs={tabs} iconColor={c.text.secondary} accent={c.accent.indigo} />
      ) : null}
    </View>
  );
}

/**
 * @deprecated — the new chat-home top bar spans the full viewport
 * width without a traffic-light placeholder slot. Kept exported for
 * back-compat with any host that still imports it.
 */
export function TrafficLightPlaceholder(): JSX.Element {
  return (
    <View
      accessibilityElementsHidden
      style={{ pointerEvents: 'none' }}
      className="w-[72px] h-[32px]"
    />
  );
}
